import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Typography, Form, Space, Row, Col } from "antd";
import { useTranslation } from "react-i18next";

import InputPassword from "src/components/form/InputPassword";
import { Routes } from "src/Router";
import { Axios, ErrorResponse } from "src/utils/Axios";
import { ContentCard } from "src/components/layout/LayoutComponents";

import { Button, CheckCircleIcon } from "./style/AuthenticationComponents";
import { usePageTitle } from "src/hooks/usePageTitle";

const { Title, Text } = Typography;

const ResetPassword = () => {
	const { t } = useTranslation();
	const [resetSuccess, setResetSuccess] = useState(false);
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();
	const { token } = useParams();
	const [form] = Form.useForm();

	usePageTitle(t("pageTitle.resetPassword"));

	const onFinish = ({ password }: { password: string }) => {
		setLoading(true);
		Axios.post("/user/account/recover", {
			password,
			token,
		})
			.then(
				() => {
					setResetSuccess(true);
				},
				(error: ErrorResponse) => {
					void Axios.error(error, t("error-messages.forgot-password-error"));
				}
			)
			.finally(() => setLoading(false));
	};

	if (resetSuccess) {
		return (
			<ContentCard>
				<CheckCircleIcon />
				<Title level={3} style={{ textAlign: "center" }}>
					{t("authentication.password-reset-success-title")}
				</Title>
				<Text type="secondary" style={{ textAlign: "center" }}>
					{t("authentication.password-reset-success-subtitle")}
				</Text>
				<Button
					type="primary"
					size="large"
					style={{ marginTop: 48 }}
					onClick={() => navigate(Routes.login)}
				>
					{t("authentication.go-to-login")}
				</Button>
			</ContentCard>
		);
	}

	return (
		<ContentCard>
			<Space direction="vertical" size={24}>
				<div>
					<Title level={3}>{t("authentication.reset-password")}</Title>
					<Text type="secondary">{t("authentication.reset-password-subtitle")}</Text>
				</div>
				<Form form={form} layout="vertical" onFinish={onFinish}>
					<InputPassword name="password" label={t("authentication.new-password")} />
					<InputPassword
						name="passwordRepeat"
						label={t("authentication.new-password-repeat")}
						errorMessage={t("error-messages.password-repeat-invalid")}
						rules={[
							{
								required: true,
								validator: (_, passwordRepeat) =>
									passwordRepeat === form.getFieldValue("password")
										? Promise.resolve()
										: Promise.reject(t("error-messages.password-repeat-invalid")),
							},
						]}
					/>
					<Row>
						<Col span={24} style={{ textAlign: "center" }}>
							<Button type="primary" size="large" htmlType="submit" loading={loading}>
								{t("authentication.reset-password-button")}
							</Button>
						</Col>
					</Row>
				</Form>
			</Space>
		</ContentCard>
	);
};

export default ResetPassword;
